import { useMemo } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import { FAQ_CATEGORIES, FAQ_ITEMS, type FaqCategoryId } from './faqs-data';

type Props = {
  activeCategory: FaqCategoryId | null;
};

export default function FaqsList({ activeCategory }: Props) {
  const theme = useTheme();

  const items = useMemo(
    () => (activeCategory ? FAQ_ITEMS.filter((i) => i.category === activeCategory) : FAQ_ITEMS),
    [activeCategory]
  );

  const activeLabel = useMemo(
    () => FAQ_CATEGORIES.find((c) => c.id === activeCategory)?.label,
    [activeCategory]
  );

  return (
    <Stack spacing={2}>
      {activeLabel && (
        <Typography
          variant="overline"
          sx={{ color: 'text.disabled', letterSpacing: 1 }}
        >
          Showing: {activeLabel}
        </Typography>
      )}

      {items.length === 0 ? (
        <EmptyContent
          title="No questions in this category yet"
          description="Try another category or get in touch using the form."
          sx={{ py: 4 }}
        />
      ) : (
        items.map((item) => (
          <Accordion
            key={item.question}
            disableGutters
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                borderColor: alpha(theme.palette.primary.main, 0.32),
              },
            }}
          >
            <AccordionSummary
              expandIcon={<Iconify icon="eva:chevron-down-fill" />}
              sx={{
                px: 2.5,
                py: 1,
                '& .MuiAccordionSummary-content': { my: 1.25 },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {item.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
              <Box
                sx={{
                  borderLeft: (t) => `3px solid ${alpha(t.palette.primary.main, 0.32)}`,
                  pl: 2,
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {item.answer}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Stack>
  );
}
